import { useState, useEffect, useRef, useCallback } from 'react';
import { Wordmark } from '../brand/Brand';
import { Icon, type IconName } from '../icons';
import type { User, Idea } from '../api';
import * as api from '../api';
import { StatusBadge, SourceIcon, TagChip } from '../components/StatusBadge';

// First-run for accounts with no ideas (spec: docs/2.0/design/first-run.md).
// Shown at /app/start; App.tsx redirects /app here while active_idea_count is 0.

export const ONBOARDING_DISMISSED_KEY = 'nr-onboarding-dismissed';

type Channel = 'claude' | 'cli' | 'ios' | 'web' | 'email';
type Step = { kind: 'pick' } | { kind: 'connect'; channel: Channel } | { kind: 'success'; idea: Idea };

const CHANNELS: { id: Channel; icon: IconName; title: string; body: string; badge?: string }[] = [
  { id: 'claude', icon: 'mcp', title: 'In Claude', badge: 'Recommended',
    body: 'Save ideas while you talk to Claude. Add the connector once; say "save this idea" and it lands here.' },
  { id: 'cli', icon: 'cli', title: 'Terminal',
    body: 'Capture from the command line. Install nrepo, sign in once, then nrepo push "…" from any repo.' },
  { id: 'ios', icon: 'ios', title: 'iPhone + Siri',
    body: 'Speak an idea on the go. Get the iOS app and say "Capture to NeuralRepo".' },
  { id: 'web', icon: 'web', title: 'Type it here', body: 'Type your first idea right here.' },
  { id: 'email', icon: 'email', title: 'Email', body: 'Forward emails to your private address.' },
];

const POLL_MS = 3_000;
const POLL_MAX_MS = 15 * 60_000;
const TROUBLESHOOT_AFTER_MS = 2 * 60_000;
const RETAG_AFTER_MS = 20_000;

function mcpUrl(): string {
  return `${window.location.origin}/mcp/v2`;
}

function CopyField({ value, label }: { value: string; label?: string }) {
  const [copied, setCopied] = useState(false);
  const copy = () => {
    navigator.clipboard.writeText(value).catch(() => {});
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };
  return (
    <div className="onboarding-copy-field">
      <code className="mono">{value}</code>
      <button className="btn btn-ghost-sm" onClick={copy} aria-label={label ?? `Copy ${value}`}>
        {copied ? '✓' : 'Copy'}
      </button>
    </div>
  );
}

/** Polls /user/me until the first idea exists, then resolves it. Pauses while the tab is hidden. */
function useFirstIdea(active: boolean, onIdea: (idea: Idea) => void) {
  const startedAt = useRef(Date.now());
  const done = useRef(false);
  const onIdeaRef = useRef(onIdea);
  onIdeaRef.current = onIdea;
  const [expired, setExpired] = useState(false);
  const [epoch, setEpoch] = useState(0);

  useEffect(() => {
    if (!active) return;
    startedAt.current = Date.now();
    let timer: ReturnType<typeof setTimeout> | undefined;
    let cancelled = false;

    const check = async () => {
      if (cancelled || done.current) return;
      if (Date.now() - startedAt.current > POLL_MAX_MS) {
        setExpired(true);
        return;
      }
      try {
        const me = await api.getMe();
        if (me.active_idea_count > 0) {
          const { ideas } = await api.listIdeas({ limit: 1 });
          if (ideas[0] && !cancelled) {
            done.current = true;
            onIdeaRef.current(ideas[0]);
            return;
          }
        }
      } catch {
        // Transient; try again on the next tick
      }
      schedule();
    };
    const schedule = () => {
      if (cancelled || document.hidden) return;
      timer = setTimeout(check, POLL_MS);
    };
    const onVisibility = () => {
      clearTimeout(timer);
      if (!document.hidden) check();
    };

    check();
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      cancelled = true;
      clearTimeout(timer);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [active, epoch]);

  const restart = useCallback(() => {
    setExpired(false);
    setEpoch((e) => e + 1);
  }, []);

  return { expired, restart, startedAt };
}

function ChannelPicker({ user, onPick }: { user: User; onPick: (c: Channel) => void }) {
  return (
    <div className="onb-pick">
      <h1 className="onb-title">Where do your ideas show up first?</h1>
      <p className="onb-lede">
        Pick one place to start. NeuralRepo files, tags and links what you capture there, and spots
        duplicates. You can add the others later.
      </p>
      {user.is_trialing && (
        <p className="onb-trial">You're on a 30-day Pro trial. No card needed.</p>
      )}
      <div className="onb-cards">
        {CHANNELS.map((c) => {
          const cliBlocked = user.plan !== 'pro' && !(user.sign_in_providers ?? []).includes('github');
          const badge = c.id === 'email'
            ? (user.plan === 'pro' ? (user.is_trialing ? 'Pro · in your trial' : undefined) : 'Pro')
            : c.id === 'cli' && cliBlocked ? 'Needs GitHub sign-in or Pro'
            : c.badge;
          return (
            <button
              key={c.id}
              className={`onb-card${c.id === 'claude' ? ' onb-card-featured' : ''}`}
              onClick={() => onPick(c.id)}
            >
              <span className="onb-card-head">
                <span className="onb-card-icon" aria-hidden="true"><Icon name={c.icon} size={18} /></span>
                <span className="onb-card-title">{c.title}</span>
                {badge && <span className="onb-card-badge mono">{badge}</span>}
              </span>
              <span className="onb-card-body">{c.body}</span>
            </button>
          );
        })}
      </div>
    </div>
  );
}

type ClaudeTab = 'web' | 'desktop' | 'code';

function ClaudeStep({ onTypeInstead }: { onTypeInstead: () => void }) {
  const [tab, setTab] = useState<ClaudeTab>('web');
  const url = mcpUrl();
  const tabs: { id: ClaudeTab; label: string }[] = [
    { id: 'web', label: 'claude.ai' },
    { id: 'desktop', label: 'Claude Desktop' },
    { id: 'code', label: 'Claude Code' },
  ];

  return (
    <div className="onb-steps-col">
      <h1 className="onb-title">Add NeuralRepo to Claude</h1>
      <div className="onb-tabs" role="tablist">
        {tabs.map((t) => (
          <button
            key={t.id}
            role="tab"
            aria-selected={tab === t.id}
            className={`onb-tab${tab === t.id ? ' onb-tab-active' : ''}`}
            onClick={() => { setTab(t.id); api.track('onboarding_step', { step: `connect:claude:${t.id}` }); }}
          >
            {t.label}
          </button>
        ))}
      </div>

      {tab === 'code' ? (
        <ol className="onb-list">
          <li>
            Run this in your terminal:
            <CopyField value={`claude mcp add --transport http neuralrepo ${url}`} label="Copy the claude mcp add command" />
          </li>
          <li>In Claude Code, type <code className="mono">/mcp</code>, pick <strong>neuralrepo</strong> and sign in.</li>
          <li>Ask Claude: <em>"Save an idea: …"</em></li>
        </ol>
      ) : (
        <>
          {tab === 'desktop' && (
            <p className="onb-note">
              Connectors you add in Claude Desktop also show up on claude.ai and Claude mobile, and the other way round.
            </p>
          )}
          <ol className="onb-list">
            <li>
              Open{' '}
              <a href="https://claude.ai/settings/connectors" target="_blank" rel="noopener noreferrer">
                Settings → Connectors
              </a>{' '}
              and choose <strong>Add custom connector</strong>.
            </li>
            <li>
              Name it <strong>NeuralRepo</strong> and paste this URL:
              <CopyField value={url} label="Copy the connector URL" />
            </li>
            <li>
              Click <strong>Connect</strong>. NeuralRepo asks you to allow Claude to access your account;
              choose <strong>Allow</strong>.
            </li>
            <li>
              In any chat, try:
              <CopyField value="Save an idea: " label="Copy the example prompt" />
            </li>
          </ol>
        </>
      )}

      <details className="onb-help">
        <summary>Not working?</summary>
        <Troubleshooting onTypeInstead={onTypeInstead} />
      </details>
    </div>
  );
}

function Troubleshooting({ onTypeInstead }: { onTypeInstead: () => void }) {
  return (
    <ul className="onb-trouble">
      <li>If Claude says the connection failed, click Connect again and choose <strong>Allow</strong>.</li>
      <li>Check that NeuralRepo is switched on in the chat's tools menu.</li>
      <li>Try the exact phrase <em>"Save an idea: …"</em>.</li>
      <li>Remove the connector and add the URL again.</li>
      <li>
        Still stuck?{' '}
        <button className="onb-link" onClick={onTypeInstead}>Type it here instead</button>.
      </li>
    </ul>
  );
}

function CliStep({ user }: { user: User }) {
  const github = (user.sign_in_providers ?? []).includes('github');
  const pro = user.plan === 'pro';
  const [key, setKey] = useState<string | null>(null);
  const [keyError, setKeyError] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);

  const createKey = async () => {
    setCreating(true);
    setKeyError(null);
    try {
      const res = await api.createApiKey('nrepo CLI');
      setKey(res.key);
    } catch (err) {
      setKeyError((err as Error).message);
    } finally {
      setCreating(false);
    }
  };

  return (
    <div className="onb-steps-col">
      <h1 className="onb-title">Capture from your terminal</h1>
      <ol className="onb-list">
        <li>
          Install the CLI:
          <CopyField value="npm install -g @neuralconfig/nrepo" label="Copy the install command" />
        </li>
        {github ? (
          <li>
            Sign in. Your browser opens to approve it:
            <CopyField value="nrepo login" label="Copy the login command" />
          </li>
        ) : pro ? (
          <li>
            Sign in with an API key:
            <CopyField value="nrepo login --api-key" label="Copy the login command" />
            {key ? (
              <>
                <span className="onb-sub">Paste this key when asked. It's shown once.</span>
                <CopyField value={key} label="Copy the API key" />
              </>
            ) : (
              <button className="btn btn-secondary onb-inline-btn" onClick={createKey} disabled={creating}>
                {creating ? 'Creating…' : 'Create a key'}
              </button>
            )}
            {keyError && <span className="onb-sub onb-error">{keyError}</span>}
          </li>
        ) : (
          <li>
            Sign in with <code className="mono">nrepo login</code>. It uses GitHub, and an API key needs Pro.
            Your account signs in another way, so{' '}
            <a href="/upgrade">upgrade to Pro</a> to use an API key, or pick another channel.
          </li>
        )}
        <li>
          Save your first idea:
          <CopyField value={'nrepo push "My first idea"'} label="Copy the push command" />
        </li>
        <li>
          Optional: add the NeuralRepo skill to Claude Code:
          <CopyField value="nrepo install-skill" label="Copy the install-skill command" />
        </li>
      </ol>
    </div>
  );
}

function IosStep({ user }: { user: User }) {
  const providers = user.sign_in_providers ?? [];
  const appSignIn = providers.includes('apple') || providers.includes('github');
  return (
    <div className="onb-steps-col">
      <h1 className="onb-title">Capture from your iPhone</h1>
      <ol className="onb-list">
        <li>
          Get the NeuralRepo app. <span className="onb-soon">Coming to the App Store soon</span>
        </li>
        <li>
          Open it and choose <strong>Sign in with Apple</strong>, or sign in with GitHub.
          {!appSignIn && (
            <span className="onb-sub">
              Use the same email as this account ({user.email}), or the app creates a separate account.
            </span>
          )}
        </li>
        <li>
          Say <em>"Capture to NeuralRepo"</em> to Siri, or share to NeuralRepo from any app.
        </li>
      </ol>
      <p className="onb-note">Until the app is out, pick another channel to save your first idea.</p>
    </div>
  );
}

function WebStep({ onCreated }: { onCreated: (idea: Idea) => void }) {
  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const save = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!title.trim()) return;
    setSaving(true);
    setError(null);
    try {
      const idea = await api.createIdea({ title: title.trim(), body: body.trim() || undefined, source: 'web' });
      onCreated(idea);
    } catch (err) {
      setError((err as Error).message);
      setSaving(false);
    }
  };

  return (
    <div className="onb-steps-col">
      <h1 className="onb-title">Type your first idea</h1>
      <form className="onb-form" onSubmit={save}>
        <label className="onb-label mono" htmlFor="onb-title">Idea</label>
        <input
          id="onb-title"
          className="onb-input"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          placeholder="e.g. A plant-watering reminder that checks the weather first"
          maxLength={500}
          autoFocus
        />
        <label className="onb-label mono" htmlFor="onb-body">Note (optional)</label>
        <textarea
          id="onb-body"
          className="onb-input onb-textarea"
          value={body}
          onChange={(e) => setBody(e.target.value)}
          rows={4}
        />
        {error && <p className="onb-sub onb-error">{error}</p>}
        <button type="submit" className="btn btn-primary onb-open" disabled={saving || !title.trim()}>
          {saving ? 'Saving…' : 'Save my first idea'}
        </button>
      </form>
    </div>
  );
}

function EmailStep({ user }: { user: User }) {
  const [address, setAddress] = useState<string | null>(null);
  const [failed, setFailed] = useState(false);
  const pro = user.plan === 'pro';

  useEffect(() => {
    if (!pro) return;
    api.getEmailCapture().then((c) => setAddress(c.address)).catch(() => setFailed(true));
  }, [pro]);

  return (
    <div className="onb-steps-col">
      <h1 className="onb-title">Capture by email</h1>
      {!pro ? (
        <p className="onb-lede">
          Email capture is part of Pro. <a href="/upgrade">Upgrade</a> to get your private address,
          or pick another channel.
        </p>
      ) : (
        <ol className="onb-list">
          <li>
            Your private capture address:
            {address
              ? <CopyField value={address} label="Copy your capture address" />
              : <span className="onb-sub">{failed ? 'Couldn\'t load it. Find it in Settings → Integrations.' : 'Loading…'}</span>}
          </li>
          <li>Forward any email to it, with a subject. The subject becomes the title, and the body becomes the note.</li>
        </ol>
      )}
      {pro && (
        <p className="onb-note">
          Keep this address private: anything sent to it is saved to your account. You can replace it
          in Settings → Integrations.
        </p>
      )}
    </div>
  );
}

function WaitingPanel({ channel, startedAt, expired, onRestart, onTypeInstead }: {
  channel: Channel;
  startedAt: React.MutableRefObject<number>;
  expired: boolean;
  onRestart: () => void;
  onTypeInstead: () => void;
}) {
  const [slow, setSlow] = useState(false);
  useEffect(() => {
    const t = setInterval(() => setSlow(Date.now() - startedAt.current > TROUBLESHOOT_AFTER_MS), 5_000);
    return () => clearInterval(t);
  }, [startedAt]);

  return (
    <aside className="onb-wait" aria-live="polite">
      <div className="onb-wait-head">
        {!expired && <span className="onb-pulse" aria-hidden="true" />}
        <strong>{expired ? 'Stopped waiting' : 'Waiting for your first idea…'}</strong>
      </div>
      <p className="onb-wait-body">
        {expired
          ? 'Nothing arrived in 15 minutes.'
          : 'Keep this tab open. The moment it\'s saved, it shows up here.'}
      </p>
      {expired && <button className="btn btn-secondary" onClick={onRestart}>Keep waiting</button>}
      <ul className="onb-checklist">
        <li className="onb-check-done">✓ Account ready</li>
        <li>○ First idea saved</li>
      </ul>
      {slow && !expired && (
        <div className="onb-wait-slow">
          <p className="onb-wait-body">Taking a while?</p>
          {channel === 'claude' ? (
            <Troubleshooting onTypeInstead={onTypeInstead} />
          ) : (
            <p className="onb-wait-body">
              <button className="onb-link" onClick={onTypeInstead}>Type it here instead</button>.
            </p>
          )}
        </div>
      )}
    </aside>
  );
}

function Success({ idea, used, onAnother, onOpen }: {
  idea: Idea;
  used: Channel | null;
  onAnother: (c: Channel) => void;
  onOpen: () => void;
}) {
  const [current, setCurrent] = useState(idea);
  const [settled, setSettled] = useState((idea.tags ?? []).length > 0);

  // Tags arrive from the queue a little later; re-fetch once.
  useEffect(() => {
    if (settled) return;
    const t = setTimeout(() => {
      api.getIdea(idea.id).then(setCurrent).catch(() => {}).finally(() => setSettled(true));
    }, RETAG_AFTER_MS);
    return () => clearTimeout(t);
  }, [idea.id, settled]);

  const others = CHANNELS.filter((c) => c.id !== used);

  return (
    <div className="onb-success">
      <h1 className="onb-title">Your first idea is in.</h1>
      <div className="onb-idea">
        <div className="onb-idea-head">
          <span className="onb-idea-num mono">#{current.number}</span>
          <SourceIcon source={current.source} />
          <StatusBadge status={current.status} />
        </div>
        <div className="onb-idea-title">{current.title}</div>
        {settled ? (
          (current.tags ?? []).length > 0 && (
            <div className="onb-idea-tags">
              {current.tags.map((t) => <TagChip key={t} name={t} />)}
            </div>
          )
        ) : (
          <p className="onb-idea-status">Tagging and checking for similar ideas…</p>
        )}
      </div>
      <button className="btn btn-primary onb-open" onClick={onOpen}>Open my ideas</button>
      <div className="onb-another">
        <div className="onb-another-label mono">Add another channel</div>
        <div className="onb-another-row">
          {others.map((c) => (
            <button key={c.id} className="btn btn-ghost-sm" onClick={() => onAnother(c.id)}>
              <span aria-hidden="true" className="onb-tab-icon"><Icon name={c.icon} size={14} /></span> {c.title}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}

export function OnboardingPage({ user, onExit }: { user: User; onExit: () => void }) {
  const [step, setStep] = useState<Step>({ kind: 'pick' });
  const [used, setUsed] = useState<Channel | null>(null);
  const [firstIdea, setFirstIdea] = useState<Idea | null>(null);

  useEffect(() => {
    api.track('onboarding_step', { step: 'welcome' });
  }, []);

  const waiting = step.kind === 'connect' && firstIdea === null;
  const arrived = (idea: Idea) => {
    setFirstIdea(idea);
    setStep({ kind: 'success', idea });
    api.track('onboarding_step', { step: 'success' });
  };
  const { expired, restart, startedAt } = useFirstIdea(waiting, arrived);

  const pick = (c: Channel) => {
    api.track('channel_selected', { channel: c });
    api.track('onboarding_step', { step: `connect:${c}` });
    if (firstIdea === null) setUsed(c);
    setStep({ kind: 'connect', channel: c });
  };

  const later = () => {
    api.track('onboarding_dismissed', { step: step.kind === 'connect' ? `connect:${step.channel}` : step.kind });
    try { localStorage.setItem(ONBOARDING_DISMISSED_KEY, '1'); } catch {}
    onExit();
  };

  const stepIndex = step.kind === 'pick' ? 0 : step.kind === 'connect' ? 1 : 2;
  const labels = ['Pick a channel', 'Connect', 'First idea'];

  return (
    <div className="onb-page">
      <header className="onb-header">
        <Wordmark size={18} />
        <ol className="onb-progress" aria-label="Setup progress">
          {labels.map((l, i) => (
            <li key={l} className={i === stepIndex ? 'onb-progress-current' : i < stepIndex ? 'onb-progress-done' : ''}>
              {i + 1} {l}
            </li>
          ))}
        </ol>
        <button
          className="onb-later"
          onClick={later}
          style={step.kind === 'success' ? { visibility: 'hidden' } : undefined}
        >
          Do this later
        </button>
      </header>

      <main className="onb-main">
        {step.kind === 'pick' && <ChannelPicker user={user} onPick={pick} />}

        {step.kind === 'connect' && (
          <div className={`onb-connect${step.channel === 'web' ? ' onb-connect-single' : ''}`}>
            <div>
              <button className="onb-back" onClick={() => setStep(firstIdea ? { kind: 'success', idea: firstIdea } : { kind: 'pick' })}>
                ← {firstIdea ? 'Back' : 'All channels'}
              </button>
              {step.channel === 'claude' && <ClaudeStep onTypeInstead={() => pick('web')} />}
              {step.channel === 'cli' && <CliStep user={user} />}
              {step.channel === 'ios' && <IosStep user={user} />}
              {step.channel === 'web' && <WebStep onCreated={arrived} />}
              {step.channel === 'email' && <EmailStep user={user} />}
            </div>
            {firstIdea === null && step.channel !== 'web' && (
              <WaitingPanel
                channel={step.channel}
                startedAt={startedAt}
                expired={expired}
                onRestart={restart}
                onTypeInstead={() => pick('web')}
              />
            )}
          </div>
        )}

        {step.kind === 'success' && (
          <Success idea={step.idea} used={used} onAnother={pick} onOpen={onExit} />
        )}
      </main>
    </div>
  );
}
